// One decrypted event as the run page's chat shows it (after Isomorphic's support-inbox
// thread): the prompt and the user's answers on the right in a light tint of the brand colour
// (S157), the agent on the left (its messages and answer as Markdown, S137, with Copy and a Raw
// view of the Markdown source, S157), tool calls as compact lines, and a locked or failed row
// when this browser cannot read it.
import { Check, CheckCircle, Code, Copy, LockKey, Robot, TextAa, Wrench, WarningCircle } from "@phosphor-icons/react";
import { type ReactNode, useState } from "react";

import { useT } from "../i18n";
import { cn } from "../ui/cn";
import { Markdown } from "../ui/markdown/Markdown";
import { clockTime } from "../workloads/format";
import { toolSummary } from "./display";
import { eventTime, eventTool } from "./hostRows";
import { displayText, type EventView } from "./model";
import { parseQuestion } from "./question";

function Frame({ event, children, className }: { event: EventView; children: ReactNode; className?: string }) {
  return (
    <article className={className} data-testid="event" data-event-type={event.type} data-content={event.content.kind}>
      {children}
    </article>
  );
}

function stamp(event: EventView): string {
  const at = eventTime(event);
  return at === undefined ? `#${event.seq}` : clockTime(at);
}

function UserBubble({ event, text }: { event: EventView; text: string }) {
  const t = useT().conversation.events;
  const own = (t.own as Partial<Record<string, string>>)[event.type];
  return (
    <Frame event={event} className="flex justify-end">
      <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-ee-md border border-bubble-user-border bg-bubble-user px-4 py-3 text-[13.5px] leading-relaxed text-gray-900">
        {text}
        <div className="mt-1.5 text-end text-[11px] text-gray-500">
          {t.you} · {own} · {stamp(event)}
        </div>
      </div>
    </Frame>
  );
}

function AgentBubble({
  event,
  agent,
  children,
  tone,
  markdown,
  actions,
}: {
  event: EventView;
  agent: string;
  children: ReactNode;
  tone?: "result";
  markdown?: boolean;
  actions?: ReactNode;
}) {
  const t = useT().conversation.events;
  return (
    <Frame event={event} className="flex gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-900 text-gray-0">
        {tone === "result" ? <CheckCircle weight="fill" className="h-[18px] w-[18px]" /> : <Robot weight="duotone" className="h-[18px] w-[18px]" />}
      </span>
      <div
        className={cn(
          "min-w-0 max-w-[85%] break-words rounded-2xl rounded-ss-md px-4 py-3 text-[13.5px] leading-relaxed text-gray-800",
          !markdown && "whitespace-pre-wrap",
          tone === "result" ? "border border-green/30 bg-green-lighter/50" : "bg-gray-100",
        )}
      >
        {children}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
          <span>
            {agent}
            {tone === "result" ? ` · ${t.result}` : ""} · {stamp(event)}
          </span>
          {actions ? <span className="ms-auto flex items-center gap-1">{actions}</span> : null}
        </div>
      </div>
    </Frame>
  );
}

const actionClass =
  "inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium text-gray-600 hover:bg-gray-0/70 hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary";

/** An agent message as Markdown, or its source as written (Raw), with Copy for the source. */
function MarkdownBubble({ event, agent, text, tone }: { event: EventView; agent: string; text: string; tone?: "result" }) {
  const t = useT().conversation.events;
  const [raw, setRaw] = useState(false);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard?.writeText(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  const actions = (
    <>
      <button type="button" className={actionClass} aria-pressed={raw} onClick={() => setRaw((r) => !r)} data-testid="toggle-raw">
        {raw ? <TextAa className="h-3.5 w-3.5" aria-hidden /> : <Code className="h-3.5 w-3.5" aria-hidden />}
        {raw ? t.formatted : t.raw}
      </button>
      <button type="button" className={actionClass} onClick={() => void copy()} data-testid="copy-markdown">
        {copied ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
        {copied ? t.copied : t.copy}
      </button>
    </>
  );
  return (
    <AgentBubble event={event} agent={agent} tone={tone} markdown={!raw} actions={actions}>
      {raw ? <pre className="m-0 whitespace-pre-wrap break-words font-mono text-[12.5px] leading-relaxed text-gray-800" data-testid="raw-markdown">{text}</pre> : <Markdown source={text} />}
    </AgentBubble>
  );
}

function ToolLine({ event, text }: { event: EventView; text: string }) {
  const t = useT().conversation.events;
  const tool = eventTool(event) ?? (event.type === "tool_result" ? t.result : t.tool);
  return (
    <Frame event={event} className="flex min-w-0 items-center gap-2.5 ps-11 text-[12.5px] text-gray-500">
      <Wrench weight="duotone" className="h-4 w-4 shrink-0" />
      <span className="shrink-0 font-mono text-gray-700">{tool}</span>
      <span className="min-w-0 truncate">{text}</span>
      <span className="ms-auto shrink-0 font-mono text-[11px]">{stamp(event)}</span>
    </Frame>
  );
}

function Unreadable({ event }: { event: EventView }) {
  const t = useT().conversation.events;
  const failed = event.content.kind === "failed";
  return (
    <Frame
      event={event}
      className={cn("flex items-center gap-2.5 rounded-lg border border-dashed px-3 py-2.5 text-[12.5px]", failed ? "border-red/40 text-red-dark" : "border-muted text-gray-500")}
    >
      {failed ? <WarningCircle weight="fill" className="h-4 w-4 shrink-0" /> : <LockKey weight="duotone" className="h-4 w-4 shrink-0 text-secondary" />}
      <span className="font-mono text-gray-700">{event.type}</span>
      <span className="min-w-0">
        {event.content.kind === "failed" ? t.couldNotDecrypt(event.content.reason) : t.noKey}
      </span>
      <span className="ms-auto shrink-0 font-mono text-[11px]">{stamp(event)}</span>
    </Frame>
  );
}

export function EventRow({ event, agent }: { event: EventView; agent: string }) {
  const t = useT().conversation.events;
  if (event.content.kind !== "plaintext") return <Unreadable event={event} />;
  const text = event.content.text;
  switch (event.type) {
    case "prompt":
    case "answer":
    case "instruction":
      return <UserBubble event={event} text={displayText(event.type, text)} />;
    case "tool_use":
    case "tool_result":
      return <ToolLine event={event} text={toolSummary(text)} />;
    case "question": {
      const asked = parseQuestion(text);
      return (
        <AgentBubble event={event} agent={agent}>
          <span className="font-semibold">{asked?.text ?? text}</span>
          {asked && asked.options.length > 0 ? <span className="block text-gray-500">{t.options(asked.options.join(" · "))}</span> : null}
        </AgentBubble>
      );
    }
    case "result":
    case "assistant":
      return <MarkdownBubble event={event} agent={agent} text={text} tone={event.type === "result" ? "result" : undefined} />;
    default:
      return (
        <AgentBubble event={event} agent={agent}>
          {text}
        </AgentBubble>
      );
  }
}
